import { useEffect, useRef, useState } from "react";
import { Instagram, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Share menu for every story on the site.
 * Options: Add to Instagram Story (1080x1920 card, QR back-link, native share
 * sheet on phones so readers pick Instagram) / Copy link / Share link.
 */
export default function StoryShare({ path, title = "City Girls Decoded" }) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const preloadRef = useRef(null);

  // The pretty article URL only carries the site-level OG tags (static build),
  // so link shares go through the per-post share route — it serves the article
  // card (artwork + spins) to crawlers and instantly redirects humans.
  const articlePath = path.replace(/\/story-card$/, "");
  const slug = articlePath.split("/").pop();
  const shareUrl = articlePath.startsWith("/rhythm/")
    ? `${API}/share/rhythm/${slug}`
    : `${API}/share/${slug}`;

  useEffect(() => {
    let url;
    fetch(`${API}${path}`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (b) {
          url = URL.createObjectURL(b);
          preloadRef.current = b;
          setBlobUrl(url);
        }
      })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [path]);

  const shareStory = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = preloadRef.current || (await fetch(`${API}${path}`)).blob();
      const storyUrl = `${window.location.origin}${window.location.pathname}`;
      const file = new File([blob], "story-card.png", { type: "image/png" });
      const shareData = { files: [file], title, text: `${title} — ${storyUrl}` };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData); // phone: reader picks Instagram
        toast.success("Now add the link sticker", {
          description:
            "In the IG composer, tap the sticker tool → LINK and paste this. Tag @citygirlsdecoded too!",
          action: {
            label: "Copy link",
            onClick: () => navigator.clipboard.writeText(storyUrl),
          },
          duration: 12000,
        });
        setOpen(false);
        return;
      }
      // Desktop: download the card + guide
      const url = blobUrl || URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "story-card.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Card saved — now add the link", {
        description:
          "Post the card to your IG story, then tap the sticker tool → LINK and paste the story link. Tag @citygirlsdecoded too!",
        action: {
          label: "Copy link",
          onClick: () => navigator.clipboard.writeText(storyUrl),
        },
        duration: 12000,
      });
      if (/iPhone|Android/i.test(navigator.userAgent)) {
        setTimeout(() => {
          window.location.href = "instagram://story-camera";
        }, 600);
      }
      setOpen(false);
    } catch (err) {
      if (err && err.name === "AbortError") return;
      toast.error("Couldn't prepare the story card — try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it anywhere.", {
        description: "X and iMessage will show the artwork card automatically.",
      });
    } catch {
      toast.error("Couldn't copy — grab it from the address bar.");
    }
    setOpen(false);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${title} — City Girls Decoded`, url: shareUrl });
        setOpen(false);
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    copyLink();
  };

  return (
    <div className="relative">
      <button
        data-testid="story-share-btn"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/75 transition-colors hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
      >
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>

      {open && (
        <>
          <button
            aria-label="Close share menu"
            data-testid="story-share-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            data-testid="story-share-menu"
            className="absolute right-0 top-full z-50 mt-2 w-64 border border-white/15 bg-[#0B0A0F] p-2 shadow-2xl shadow-black/60"
          >
            <button
              data-testid="story-share-story"
              onClick={shareStory}
              disabled={busy}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:bg-white/5 hover:text-[#F3F1ED] disabled:opacity-50"
            >
              <Instagram className="h-4 w-4 shrink-0" />
              {busy ? "Preparing…" : "Add to Instagram Story"}
            </button>
            <button
              data-testid="story-share-copy"
              onClick={copyLink}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:bg-white/5 hover:text-[#F3F1ED]"
            >
              <Link2 className="h-4 w-4 shrink-0" /> Copy link
            </button>
            <button
              data-testid="story-share-more"
              onClick={shareLink}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:bg-white/5 hover:text-[#F3F1ED]"
            >
              <Share2 className="h-4 w-4 shrink-0" /> More options…
            </button>
          </div>
        </>
      )}
    </div>
  );
}
